import {HOME_META_DATA} from '@/util/metaData/company';
import Head from 'next/head';
import PageHead from '@/components/PageHead';
import React from 'react';
import HomeBanner from '@/components/organisms/hero/HomeBanner';
import ImpactStats from '@/components/organisms/stats/ImpactStats';
import {
    homeWhyChooseData,
    trustedByBrands,
    EngagementModelsTrustBadges,
    clientFeedbackData,
    clients,
    ImpactStatsData,
    HomeServicesV2Data,
    partnershipData,
    AIModelIntegrationData,
    homeIndustryTabsData,
    homeFaqData,
    HomeDeliveryGuaranteeData,
    ComplianceStandardsData,
    EngagementModelsData,
    HomeDeliveryProcessData,
    LeadershipInsightsData,
    HomeExpertiseShowcaseData
} from '@/util/contents/home-data';
import {homeCaseStudyTabsData} from '@/util/contents/latest-portfolio-data';
import TrustedByStrip from '@/components/organisms/logos/TrustedByStrip';
import {getHomepageSchemas} from '@/schemas/pages/homepage/homepage.schema';
import {buildSchemaGraph} from '@/schemas/generators/buildSchemaGraph';
import CvSection from '@/components/atoms/CvSection';
import {getLatestInsights} from '@/Services/latestInsights';
import type {LatestInsightPost} from '@/types/listing';
import {cdnUrl} from '@/util/cdnImage';

import HomeServicesV2 from '@/components/organisms/cards/HomeServicesV2';
import ClientServed from '@/components/organisms/logos/ClientServed';
import HomeImageCTA from '@/components/organisms/cta/HomeImageCTA';
import HomePartnership from '@/components/organisms/logos/HomePartnership';
import ClientVideoFeedback from '@/components/organisms/testimonials/ClientVideoFeedback';
import LatestInsights from '@/components/organisms/insights/LatestInsights';
import HomeCaseStudyTabs from '@/components/organisms/portfolio/HomeCaseStudyTabs';
import AIModelIntegration from '@/components/organisms/logos/AIModelIntegration';
import HomeExpertiseShowcase from '@/components/organisms/cards/HomeExpertiseShowcase';
import HomeIndustryTabs from '@/components/organisms/tabs/HomeIndustryTabs';
import HomeFAQSplit from '@/components/organisms/accordions/HomeFAQSplit';
import HomeDeliveryGuarantee from '@/components/organisms/cards/HomeDeliveryGuarantee';
import ComplianceStandards from '@/components/organisms/tabs/ComplianceStandards';
import CertificationsSection from '@/components/organisms/logos/CertificationsSection';
import HomeProcessWheel from '@/components/organisms/process/HomeProcessWheel';
import LeadershipInsights from '@/components/organisms/testimonials/LeadershipInsights';
import PricingPlans from '@/components/organisms/pricing/PricingPlans';
import HomeWhyChoose from '@/components/organisms/cards/HomeWhyChoose';
const bannerVideo = cdnUrl('home-page-updated-assets/homepage-video.mp4');
const bannerVideoPoster = cdnUrl('home-page-updated-assets/home-video-thumbnail-image.webp');

// ─── getStaticProps ───────────────────────────────────────────────────────────
// Only build-time / ISR data goes through props. Static section content is imported
// directly above: every prop is serialized into __NEXT_DATA__, so passing static copy
// here would ship it twice in the HTML (once rendered, once as JSON).
export async function getStaticProps() {
    const serializedSchema = buildSchemaGraph(getHomepageSchemas({pageTitle: HOME_META_DATA.title}));

    // Server-render the "Our Latest Insights" cards (category 19) so titles/links
    // are in the initial HTML and crawlable, instead of being fetched after hydration.
    const latestInsightsData: LatestInsightPost[] = await getLatestInsights('19');

    return {
        // ISR: refresh the page (and its blog cards) hourly without a full rebuild.
        revalidate: 3600,
        props: {
            latestInsightsData,
            serializedSchema
        }
    };
}

// ─── Component ────────────────────────────────────────────────────────────────
const NewHomePage = (props: any) => {
    const {openForm, latestInsightsData, serializedSchema} = props;

    return (
        <div>
            <PageHead meta={HOME_META_DATA} url="" schema={serializedSchema ?? undefined} />
            {/* DMCA ownership verification — homepage only */}
            <Head>
                <meta name="dmca-site-verification" content="ZGRMNFRNR0IyTldmMVphcndRNnFQdWZ3NmdhN01iY1pCVUdKTWdYQ3VTUT01" />
            </Head>
            <div>
                <HomeBanner
                    heading="AI-Augmented Product Engineering for Strategic Digital Transformation"
                    subheading="Since 2011, Debut Infotech has enabled enterprises to modernize with confidence. Our AI-led product engineering delivers secure, scalable, and compliant digital products built for sustained business value."
                    buttonText="View Our Success Stories"
                    handleClickOpen={openForm}
                    backgroundVideo={bannerVideo}
                    videoPoster={bannerVideoPoster}
                    videoAlt="Redefining software design and development with AI - Homepage banner video demonstrating innovative AI-powered development strategies"
                />
            </div>
            <div className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <TrustedByStrip label="Featured in" brands={trustedByBrands} />
            </div>
            <div>
                <ImpactStats contentData={ImpactStatsData} />
            </div>
            <div className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding bg-blue-background">
                <HomeServicesV2
                    heading="Engineering Capabilities for Building, Scaling, and Modernizing Digital Products"
                    subheading="For over 15 years, Debut Infotech has helped startups, enterprises, and global organizations build, scale, and modernize digital products. Our multidisciplinary teams combine expertise in AI, <a target='_blank' href='/custom-software-development-company'>software engineering</a>, blockchain, cloud infrastructure, and DevOps to deliver secure, scalable, and business-focused technology solutions."
                    contentData={HomeServicesV2Data}
                />
            </div>

            <CvSection className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <HomeImageCTA
                    heading="Good Ideas Need Great Engineering Behind Them"
                    subheading="From AI-powered applications and blockchain platforms to enterprise software and digital products, our team delivers secure, scalable, and future-ready solutions tailored to your business goals."
                    buttonText="Discuss Your Project"
                    imageSrc={cdnUrl('home-page-updated-assets/homepage-cta-banner1.webp')}
                    imageAlt="Robotic hand stacking glass blocks marked with AI, blockchain, code and cloud icons"
                    light
                    openForm={openForm}
                />
            </CvSection>
            {/* Deliberately NOT wrapped in CvSection. This section is ~6 viewports tall and
                scroll-driven: `content-visibility: auto` would report the 1200px placeholder
                height while it is offscreen, which is the height ScrollTrigger measures its
                range from at mount — the tabs then never track the real section. The paint
                containment it implies also fights the inner `position: sticky`. */}
            <HomeCaseStudyTabs heading="Debut Infotech's Success Stories" data={homeCaseStudyTabsData} />
            <CvSection className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding bg-blue-background">
                <ClientVideoFeedback
                    heading="Client Feedback That Helps Us Improve and Build Better"
                    subheading="Real experiences from the people and businesses we've helped transform ideas into impactful digital solutions."
                    contentData={clientFeedbackData}
                />
            </CvSection>
            <CvSection className="top-space mobile-top-padding">
                <HomeImageCTA
                    heading="From $12B in NDAX volume to 80% query automation for a legal client, we deliver measurable business impact."
                    subheading="From crypto platforms to AI automation, our engineering turns complex requirements into scalable, high-impact products."
                    buttonText="Let's Architect Your Success Story"
                    imageSrc={cdnUrl('home-page-updated-assets/homepage-cta-banner-2.webp')}
                    imageAlt="Crystalline bar chart with a rising arrow representing client growth we have engineered"
                    openForm={openForm}
                />
            </CvSection>
            <CvSection className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <ClientServed heading="Clients We've Served" contentData={clients} />
            </CvSection>
            <CvSection>
                <HomeIndustryTabs
                    heading="Solving Real Business Challenges Across Various Industries"
                    subheading="Our industry experience combines domain knowledge with <strong>digital engineering</strong> expertise to build technology around sector-specific workflows, data requirements, integrations, compliance needs, and operating environments."
                    data={homeIndustryTabsData}
                />
            </CvSection>
            <CvSection>
                <HomeExpertiseShowcase
                    heading="Advanced AI Engineering for Production-Scale Systems"
                    subheading="We design and engineer AI systems spanning models, enterprise data, intelligent workflows, and autonomous execution, helping businesses deploy advanced AI capabilities in secure, scalable production environments."
                    panels={HomeExpertiseShowcaseData}
                />
            </CvSection>
            <CvSection>
                <AIModelIntegration
                    heading="Integrating Industry-Leading AI Models to Deliver Scalable Business Solutions"
                    subheading="Our <strong>AI Engineering Services</strong> span generative AI, agentic systems, RAG, enterprise automation, and LLM-powered applications across industries. Our engineers combine leading <a href = '/blog/a-comprehensive-guide-to-understanding-and-using-ai-models' target = '_blank'>AI models</a>, orchestration frameworks, and cloud platforms to build <strong>Enterprise AI</strong> agents, copilots, and automation solutions designed for scalable deployment and long-term operational and commercial value."
                    contentData={AIModelIntegrationData}
                />
            </CvSection>
            <CvSection className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding" intrinsicSize="1600px">
                <HomeWhyChoose
                    heading="Why Choose Debut Infotech as your Technology Partner?"
                    subheading="Debut Infotech works as a strategic co-creator, helping organizations move from concept to lasting digital enterprise. We integrate market intelligence with disciplined engineering to design platforms that scale, generate measurable returns, and remain resilient in changing markets. Our teams focus on outcomes such as growth, profitability, and long-term relevance, ensuring every solution delivers sustained value and positions clients to lead in an increasingly competitive and technology-driven world."
                    data={homeWhyChooseData}
                />
            </CvSection>
            <CvSection className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding bg-blue-background">
                <HomeProcessWheel
                    heading="Our Approach to Building Production-Ready Products"
                    subheading="We follow a structured engineering process that aligns strategy, architecture, development, testing, and deployment around your business goals. Each stage is designed to reduce delivery risk, maintain technical quality, and move products efficiently from initial requirements to production-ready release."
                    steps={HomeDeliveryProcessData}
                />
            </CvSection>
            <CvSection>
                <LeadershipInsights
                    heading="Strategic Insights from Leadership Driving Real-World Execution"
                    subheading="Insights shaped by leaders directly involved in delivering complex software, AI, and blockchain solutions, grounded in real execution rather than recycled industry commentary."
                    quotes={LeadershipInsightsData}
                />
            </CvSection>

            <CvSection className="bg-blue-background">
                <HomeDeliveryGuarantee
                    heading="Our Commitment to Every Engagement"
                    subheading="We establish clear timelines, milestones, and delivery expectations before development begins. Every engagement follows a structured execution plan designed to maximize speed, accountability, and predictability."
                    cards={HomeDeliveryGuaranteeData.cards}
                    ctaText={HomeDeliveryGuaranteeData.ctaText}
                    ctaUrl={HomeDeliveryGuaranteeData.ctaUrl}
                />
            </CvSection>
            <CvSection>
                <ComplianceStandards
                    heading="Security and Compliance Standards We Follow for Enterprise-Grade Solutions"
                    subheading="Every system we deliver is built against a defined compliance baseline from the first architecture decision. Regulatory alignment is not a final checklist; it is a continuous engineering discipline applied across every layer of the stack."
                    rows={ComplianceStandardsData.rows}
                />
            </CvSection>
            <CvSection>
                <CertificationsSection />
            </CvSection>

            <CvSection>
                <PricingPlans
                    heading="Flexible Engagement Models for Different Project Needs"
                    subheading="Our engagement models are structured around scope clarity, delivery requirements, and the level of flexibility your project needs. From well-defined builds to evolving product roadmaps and long-term engineering support, each model provides clear commercial terms, delivery ownership, and budget visibility."
                    cards={EngagementModelsData}
                    trustBadges={EngagementModelsTrustBadges}
                    ctaText="View Our Pricing Models"
                    ctaUrl="/pricing-models"
                />
            </CvSection>

            <CvSection className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding bg-blue-background">
                <HomePartnership contentData={partnershipData} heading="Partnerships & Alliances" />
            </CvSection>
            <CvSection className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding">
                <HomeImageCTA
                    heading="Ready to Turn Technology Investment Into Measurable Business Impact?"
                    subheading="Engage with a team that takes ownership from planning through deployment, ensuring performance, scalability, and long-term reliability."
                    buttonText="Book A Strategy Session With Experts"
                    imageSrc={cdnUrl('home-page-updated-assets/homepage-cta-banner3.webp')}
                    imageAlt="Rising bar chart with an upward arrow representing measurable business impact"
                    openForm={openForm}
                />
            </CvSection>

            <CvSection className="bottom-spacing mobile-padding-bottom">
                <HomeFAQSplit
                    heading="Frequently Asked Questions"
                    subheading="Answers to the most common questions we receive from founders, product teams, and enterprise leaders evaluating Debut Infotech."
                    faqData={homeFaqData}
                />
            </CvSection>
            <CvSection className="latest-insights top-space bottom-spacing bg-blue-background mobile-top-padding mobile-bottom-padding">
                <LatestInsights id="19" posts={latestInsightsData} reserveImageRatio />
            </CvSection>
        </div>
    );
};

export default NewHomePage;
NewHomePage.Layout = 'Main';
